import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setAgentToken } from '../../store/slices/authSlice';
import axios from '../../api/axiosConfig';
import './LoginPage.css'; // Reuse the login page styles

function AgentSignup() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Step management: 1 = Form, 2 = OTP Verification, 3 = Success
  const [step, setStep] = useState(1);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    npn: '',
    password: '',
    confirmPassword: ''
  });
  
  const [otpCode, setOtpCode] = useState('');
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  // Fetch campaigns on component mount
  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        console.log('[SIGNUP] Fetching available campaigns...');
        const response = await axios.get('/api/public/campaigns');
        
        if (response.data.success && response.data.campaigns) {
          setCampaigns(response.data.campaigns?.filter(c => c.campaignId === "aca"));
          console.log(`[SIGNUP] Loaded ${response.data.campaigns.length} campaigns`);
          
          // Auto-select ACA by default if it exists
          const acaCampaign = response.data.campaigns.find(c => 
            c.campaignId.toLowerCase() === 'aca'
          );
          if (acaCampaign) {
            setSelectedCampaigns([acaCampaign.campaignId]);
          }
        }
      } catch (err) {
        console.error('[SIGNUP] Error fetching campaigns:', err);
        setError('Failed to load campaigns. Please refresh the page.');
      } finally {
        setLoadingCampaigns(false);
      }
    };

    fetchCampaigns();
  }, []);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const toggleCampaign = (campaignId) => {
    setSelectedCampaigns(prev => {
      if (prev.includes(campaignId)) {
        return prev.filter(id => id !== campaignId);
      } else {
        return [...prev, campaignId];
      }
    });
    setError(''); 
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  // STEP 1: Form validation and send OTP
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Basic validation
    if (!formData.firstName || !formData.lastName || !formData.email || 
        !formData.phone || !formData.npn || !formData.password || !formData.confirmPassword) {
      setError('All fields are required');
      setLoading(false);
      return;
    }

    // Campaign validation
    if (selectedCampaigns.length === 0) {
      setError('Please select at least one campaign');
      setLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      setLoading(false);
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    // Phone validation — US numbers only (+1, 10-digit)
    // ⭐ BYPASS: 424242424 is the test phone — skips SMS
    const BYPASS_PHONE = '424242424';
    const cleanedPhone = formData.phone.replace(/\D/g, '');
    const isUSPhone = cleanedPhone.length === 10 ||
                      (cleanedPhone.length === 11 && cleanedPhone[0] === '1');
    if (cleanedPhone !== BYPASS_PHONE && !isUSPhone) {
      setError('Only US phone numbers are accepted. Please enter a 10-digit US number (e.g. 555-867-5309).');
      setLoading(false);
      return;
    }

    try {
      // Step 1: Check for duplicates (email + phone)
      console.log('[SIGNUP] Checking for duplicates...');
      const checkResponse = await axios.post('/api/auth/check-duplicate', {
        email: formData.email,
        phone: cleanedPhone,
        campaigns: selectedCampaigns
      });

      if (!checkResponse.data.success) {
        setError(checkResponse.data.error || 'Duplicate check failed');
        setLoading(false);
        return;
      }

      // Step 2: Send OTP to phone
      console.log('[SIGNUP] Sending OTP to phone...');
      const otpResponse = await axios.post('/api/auth/send-signup-otp', {
        phone: cleanedPhone,
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName
      });

      if (!otpResponse.data.success) {
        setError(otpResponse.data.error || 'Failed to send OTP');
        setLoading(false);
        return;
      }

      console.log('[SIGNUP] OTP sent successfully!');
      
      // Show devCode in development
      if (otpResponse.data.devCode) {
        console.log(`[SIGNUP] Dev OTP Code: ${otpResponse.data.devCode}`);
      }

      // Move to Step 2 (OTP Verification)
      setStep(2);
      setResendCooldown(60); // 60 second cooldown
      
    } catch (err) {
      console.error('[SIGNUP] Error:', err);
      setError(err.response?.data?.error || 'Failed to process signup. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // STEP 2: Verify OTP and create agent
  const handleOtpVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // ⭐ BYPASS: master test code skips OTP verification entirely
    const BYPASS_CODE = '424242424';
    const isBypass = otpCode === BYPASS_CODE;

    if (!otpCode || (otpCode.length !== 6 && !isBypass)) {
      setError('Please enter the 6-digit code');
      setLoading(false);
      return;
    }

    try {
      const cleanedPhone = formData.phone.replace(/\D/g, '');

      if (!isBypass) {
        // Step 1: Verify OTP (skipped for bypass)
        console.log('[SIGNUP] Verifying OTP...');
        const verifyResponse = await axios.post('/api/auth/verify-signup-otp', {
          phone: cleanedPhone,
          code: otpCode,
          email: formData.email
        });

        if (!verifyResponse.data.success) {
          setError(verifyResponse.data.error || 'Invalid or expired code');
          setLoading(false);
          return;
        }
        console.log('[SIGNUP] OTP verified!');
      } else {
        console.log('[SIGNUP] ⚡ Bypass code used — skipping OTP verification');
      }

      // Step 2: Create pending agent (OTP verified!)
      console.log('[SIGNUP] Creating agent...');
      const signupResponse = await axios.post('/api/auth/agent/signup', {
        ...formData,
        phone: cleanedPhone,
        campaigns: selectedCampaigns
      });

      if (!signupResponse.data.success) {
        setError(signupResponse.data.error || 'Failed to create account');
        setLoading(false);
        return;
      }

      console.log('[SIGNUP] ✅ Account created successfully!');
      
      // ⭐ Auto-login: store the JWT token returned by signup
      if (signupResponse.data.token) {
        dispatch(setAgentToken(signupResponse.data.token));
        console.log('[SIGNUP] ✅ Token stored - redirecting to onboarding...');
        // Navigate directly to onboarding (no delay needed)
        navigate('/agent/onboarding');
      } else {
        // Fallback: no token returned, show success and redirect to login
        setStep(3);
        setTimeout(() => {
          navigate('/agent/login');
        }, 3000);
      }
      
    } catch (err) {
      console.error('[SIGNUP] OTP Verification Error:', err);
      setError(err.response?.data?.error || 'Failed to verify code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP
  const handleResendOTP = async () => {
    if (resendCooldown > 0) {
      return; // Still in cooldown
    }

    setError('');
    setLoading(true);

    try {
      const cleanedPhone = formData.phone.replace(/\D/g, '');
      
      const response = await axios.post('/api/auth/send-signup-otp', {
        phone: cleanedPhone,
        email: formData.email
      });

      if (response.data.success) {
        console.log('[SIGNUP] OTP resent successfully!');
        
        // Show devCode in development
        if (response.data.devCode) {
          console.log(`[SIGNUP] Dev OTP Code: ${response.data.devCode}`);
        }
        
        setResendCooldown(60); // Reset cooldown
      } else {
        setError(response.data.error || 'Failed to resend OTP');
      }
    } catch (err) {
      console.error('[SIGNUP] Resend OTP Error:', err);
      setError(err.response?.data?.error || 'Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  // STEP 1: Registration Form
  if (step === 1) {
    return (
      <div className="login-page">
        <div className="login-container" style={{ maxWidth: '500px' }}>
          <div className="login-header">
            <h1>Agent Registration</h1>
            <p>Create your account to get started</p>
          </div>

          {error && (
            <div className="error-message" style={{
              padding: '12px',
              marginBottom: '20px',
              backgroundColor: '#fee',
              border: '1px solid #fcc',
              borderRadius: '4px',
              color: '#c33'
            }}>
              {error}
            </div>
          )}

          <form onSubmit={handleFormSubmit}>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>First Name *</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="John"
                  maxLength={50}
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}
                  required
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Last Name *</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="Doe"
                  maxLength={50}
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="john@example.com"
                maxLength={254}
                required
              />
            </div>

            <div className="form-group">
              <label>Phone Number *</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="1234567890"
                maxLength={15}
                required
              />
              <small style={{ color: '#666', fontSize: '12px' }}>
                📱 We'll send a verification code to this number
              </small>
            </div>

            <div className="form-group">
              <label>NPN *</label>
              <input
                type="text"
                name="npn"
                value={formData.npn}
                onChange={handleChange}
                placeholder="Your National Producer Number"
                maxLength={20}
                required
              />
            </div>

            <div className="form-group">
              <label>Password * (min 8 characters)</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="••••••••"
                minLength={8}
                maxLength={128}
                required
              />
            </div>

            <div className="form-group">
              <label>Confirm Password *</label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="••••••••"
                minLength={8}
                maxLength={128}
                required
              />
            </div>

            {/* Campaign Selection */}
            {loadingCampaigns ? (
              <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                Loading campaigns...
              </div>
            ) : campaigns.length > 0 ? (
              <div className="form-group select-campaigns">
                <label>Select Campaigns *</label>
                <div style={{ 
                  border: '1px solid #ddd', 
                  borderRadius: '4px', 
                  padding: '10px',
                  maxHeight: '150px',
                  overflowY: 'auto'
                }}>
                  {campaigns.map(campaign => (
                    <label key={campaign.campaignId} style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      padding: '8px',
                      cursor: 'pointer',
                      borderRadius: '4px',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <input
                        type="checkbox"
                        checked={selectedCampaigns.includes(campaign.campaignId)}
                        onChange={() => toggleCampaign(campaign.campaignId)}
                        style={{ marginRight: '10px', flexShrink: 0 }}
                      />
                      <span>{campaign.name}</span>
                    </label>
                  ))}
                </div>
                {selectedCampaigns.length === 0 && (
                  <small style={{ color: '#c33', fontSize: '12px' }}>
                    Please select at least one campaign
                  </small>
                )}
              </div>
            ) : null}

            <button 
              type="submit" 
              className="btn-primary"
              disabled={loading || selectedCampaigns.length === 0}
              style={{ 
                width: '100%', 
                marginTop: '20px',
                opacity: (loading || selectedCampaigns.length === 0) ? 0.6 : 1
              }}
            >
              {loading ? 'Processing...' : 'Signup'}
            </button>

            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <p style={{ color: '#666' }}>
                Already have an account?{' '}
                <a href="/agent/login" style={{ color: '#3FA45F', textDecoration: 'none' }}>
                  Login
                </a>
              </p>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // STEP 2: OTP Verification
  if (step === 2) {
    const maskedPhone = formData.phone.replace(/(\d{3})(\d{3})(\d{4})/, '($1) ***-**$3');
    
    return (
      <div className="login-page">
        <div className="login-container" style={{ maxWidth: '450px' }}>
          <div className="login-header">
            <h1>📱 Verify Your Phone</h1>
            <p>We've sent a 6-digit code to <strong>{maskedPhone}</strong></p>
          </div>

          <div style={{
            background: '#fffbeb',
            border: '1px solid #fcd34d',
            borderRadius: '8px',
            padding: '10px 13px',
            marginBottom: '16px',
            fontSize: '12px',
            color: '#92400e',
            lineHeight: '1.55'
          }}>
            💡 <strong>Can't find the code?</strong> Check your <strong>Unknown / Filtered messages</strong> folder — the text comes from <strong>+1 (952) 299-0268</strong>.
          </div>

          {error && (
            <div className="error-message" style={{
              padding: '12px',
              marginBottom: '20px',
              backgroundColor: '#fee',
              border: '1px solid #fcc',
              borderRadius: '4px',
              color: '#c33'
            }}>
              {error}
            </div>
          )}

          <form onSubmit={handleOtpVerify}>
            <div className="form-group">
              <label>Enter 6-Digit Code *</label>
              <input
                type="text"
                value={otpCode}
                onChange={(e) => {
                  // Allow up to 9 digits (to accommodate the bypass code)
                  const value = e.target.value.replace(/\D/g, '').slice(0, 9);
                  setOtpCode(value);
                  setError('');
                }}
                placeholder="123456"
                maxLength={9}
                style={{
                  fontSize: '24px',
                  letterSpacing: '8px',
                  textAlign: 'center',
                  fontWeight: 'bold'
                }}
                autoFocus
                required
              />
            </div>

            <button 
              type="submit" 
              className="btn-primary"
              disabled={loading || (otpCode.length !== 6 && otpCode !== '424242424')}
              style={{ 
                width: '100%',
                opacity: (loading || (otpCode.length !== 6 && otpCode !== '424242424')) ? 0.6 : 1
              }}
            >
              {loading ? 'Verifying...' : 'Verify & Create Account'}
            </button>

            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              {resendCooldown > 0 ? (
                <p style={{ color: '#666' }}>
                  Resend code in {resendCooldown}s
                </p>
              ) : (
                <button
                  type="button"
                  onClick={handleResendOTP}
                  disabled={loading}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#3FA45F',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    fontSize: '14px'
                  }}
                >
                  Resend Code
                </button>
              )}
            </div>

            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <button
                type="button"
                onClick={() => {
                  setStep(1);
                  setOtpCode('');
                  setError('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#666',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                ← Back to form
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // STEP 3: Success Message
  if (step === 3) {
    return (
      <div className="login-page">
        <div className="login-container" style={{ maxWidth: '450px' }}>
          <div className="login-header">
            <h1>🎉 Registration Complete!</h1>
          </div>
          <div className="success-message" style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{ fontSize: '64px', marginBottom: '20px' }}>✅</div>
            <h2 style={{ color: '#28a745', marginBottom: '20px' }}>Account Created Successfully!</h2>
            <p style={{ fontSize: '16px', marginBottom: '10px', color: '#666' }}>
              Your account is pending admin approval.
            </p>
            <p style={{ fontSize: '14px', color: '#999', marginBottom: '30px' }}>
              You'll receive an email once your account is activated.
            </p>
            <p style={{ fontSize: '14px', color: '#666' }}>
              Redirecting to login page...
            </p>
            <div className="spinner" style={{ 
              margin: '20px auto',
              width: '40px',
              height: '40px',
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #3FA45F',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

export default AgentSignup;
